/**
 * What the music is doing right now, as four numbers and an onset flag.
 *
 * Tapped off the music gain as a **side branch**: the analyser is connected to
 * but never connects onward, so it observes the signal without being in the path
 * that reaches the speakers. Nothing here can change what the player hears, and
 * if this class throws or is never constructed the song plays identically.
 *
 * It is also deliberately not a clock. Everything timed — spawning, judging,
 * the beat grid — reads `MusicClock`, which is anchored to the audio context's
 * own scheduling. This only answers "how loud, in which bands", which is a
 * question about the present frame and cannot drift.
 *
 * The bands are split by frequency, not by bin count. An FFT's bins are linear
 * in frequency while hearing is roughly logarithmic, so an even three-way split
 * of the bins would put almost everything musical into "bass" and leave the top
 * two thirds reporting cymbals and silence.
 */

/** Band edges in Hz. Below `bassMax` is bass; above `midMax` is treble. */
const BASS_MAX_HZ = 250;
const MID_MAX_HZ = 2000;

export interface MusicAnalyserOptions {
  readonly context: AudioContext;
  /** The node to observe. The engine's music gain, normally. */
  readonly source: AudioNode;
  readonly fftSize?: number;
  /** How quickly the analyser's own smoothing follows the signal, 0..1. */
  readonly smoothing?: number;
}

export interface MusicLevels {
  /** Overall loudness, 0..1. */
  readonly amplitude: number;
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  /** 0..1, non-zero on the frame a transient is detected. */
  readonly onset: number;
}

const SILENT: MusicLevels = { amplitude: 0, bass: 0, mid: 0, treble: 0, onset: 0 };

export class MusicAnalyser {
  private readonly analyser: AnalyserNode;
  /**
   * Typed as backed by a plain `ArrayBuffer`, not `ArrayBufferLike`.
   *
   * `getByteFrequencyData` refuses a possibly-shared buffer, and the default
   * `Uint8Array` element type is now the looser one — so the annotation is what
   * makes the call type-check rather than a cast at the call site.
   */
  private readonly spectrum: Uint8Array<ArrayBuffer>;
  private readonly bassEnd: number;
  private readonly midEnd: number;

  /** Rolling mean of spectral flux, which is what an onset is measured against. */
  private fluxAverage = 0;
  private previous: Float32Array;
  private levels: MusicLevels = SILENT;
  private connected = false;

  constructor(private readonly options: MusicAnalyserOptions) {
    const { context, source } = options;
    this.analyser = context.createAnalyser();
    this.analyser.fftSize = options.fftSize ?? 512;
    this.analyser.smoothingTimeConstant = options.smoothing ?? 0.72;

    const bins = this.analyser.frequencyBinCount;
    this.spectrum = new Uint8Array(bins);
    this.previous = new Float32Array(bins);

    // Bin n covers n * (sampleRate / fftSize) Hz.
    const hzPerBin = context.sampleRate / this.analyser.fftSize;
    this.bassEnd = Math.max(1, Math.min(bins, Math.round(BASS_MAX_HZ / hzPerBin)));
    this.midEnd = Math.max(this.bassEnd + 1, Math.min(bins, Math.round(MID_MAX_HZ / hzPerBin)));

    source.connect(this.analyser);
    this.connected = true;
  }

  /** The last sampled levels, without sampling again. */
  get current(): MusicLevels {
    return this.levels;
  }

  /**
   * Reads the spectrum once.
   *
   * Called once a frame by the runtime. Sampling more than once per frame is
   * harmless but wasteful; not sampling at all leaves the previous values, which
   * is the right behaviour for a paused or finished song.
   */
  sample(): MusicLevels {
    if (!this.connected) return this.levels;
    this.analyser.getByteFrequencyData(this.spectrum);

    let bass = 0;
    let mid = 0;
    let treble = 0;
    let flux = 0;

    for (let bin = 0; bin < this.spectrum.length; bin += 1) {
      const value = (this.spectrum[bin] as number) / 255;
      if (bin < this.bassEnd) bass += value;
      else if (bin < this.midEnd) mid += value;
      else treble += value;

      // Spectral flux: only rises count. A transient is energy appearing, and
      // counting falls as well would make every note-off look like a note-on.
      const rise = value - (this.previous[bin] as number);
      if (rise > 0) flux += rise;
      this.previous[bin] = value;
    }

    const bassBins = this.bassEnd;
    const midBins = this.midEnd - this.bassEnd;
    const trebleBins = Math.max(1, this.spectrum.length - this.midEnd);

    const normalisedBass = clamp01(bass / bassBins);
    const normalisedMid = clamp01(mid / midBins);
    const normalisedTreble = clamp01(treble / trebleBins);
    const amplitude = clamp01((bass + mid + treble) / this.spectrum.length);

    // Compared against its own recent average rather than a fixed threshold, so
    // a quiet passage still produces onsets and a loud one is not permanently on.
    const threshold = this.fluxAverage * 1.5 + 0.02;
    const onset = flux > threshold ? clamp01((flux - threshold) / (threshold + 1)) : 0;
    this.fluxAverage += (flux - this.fluxAverage) * 0.12;

    this.levels = { amplitude, bass: normalisedBass, mid: normalisedMid, treble: normalisedTreble, onset };
    return this.levels;
  }

  /** Between songs: the next run must not inherit the last one's transients. */
  reset(): void {
    this.previous = new Float32Array(this.analyser.frequencyBinCount);
    this.fluxAverage = 0;
    this.levels = SILENT;
  }

  dispose(): void {
    if (!this.connected) return;
    try {
      this.options.source.disconnect(this.analyser);
    } catch {
      // Already torn down with the graph; nothing to release.
    }
    this.analyser.disconnect();
    this.connected = false;
    this.levels = SILENT;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
